import type { PropsWithChildren } from "react";
import { addYears } from "date-fns";
import type { UseFormReturn } from "react-hook-form";
import { t } from "ttag";

import {
  SessionCategoryField,
  SessionCoachField,
  SessionDurationField,
  SessionStartTimeField,
} from "@/features/coaching/components";
import type { Session } from "@/features/coaching/models";
import { SESSION_TITLE_MAX_LENGTH } from "@/features/coaching/models";
import { NOW } from "@/shared/models/legacy-constants";
import { DatePicker, Form, Input } from "@/shared/ui";

type FieldsValue = Pick<
  Session,
  "category" | "coachId" | "duration" | "startAt" | "title"
>;

type SessionFieldsForm<T> = T extends FieldsValue ? UseFormReturn<T> : never;

type SessionFieldsProps<T extends FieldsValue> = PropsWithChildren & {
  form: SessionFieldsForm<T>;
};

export const SessionFields = <T extends FieldsValue>({
  children,
  form,
}: SessionFieldsProps<T>) => {
  return (
    <Form.Provider {...form}>
      <SessionCategoryField control={form.control} />

      <Form.Field
        control={form.control}
        defaultValue=""
        name="title"
        render={({ field }) => (
          <Form.Item>
            <Form.Label aria-required>{t`Title`}</Form.Label>
            <Form.Control>
              <Input.Root
                placeholder={t`E.g. Emotional hunger`}
                maxLength={SESSION_TITLE_MAX_LENGTH}
                {...field}
              />
            </Form.Control>
            <Form.Message compact={false} />
          </Form.Item>
        )}
      />

      <Form.Field
        control={form.control}
        name="startAt"
        render={({ field: { ref: _, ...field } }) => (
          <Form.Item>
            <Form.Label aria-required>{t`Date`}</Form.Label>
            <Form.Control>
              <DatePicker
                {...field}
                startMonth={NOW}
                endMonth={addYears(NOW, 1)}
                disabledDays={{ before: NOW }}
              />
            </Form.Control>
            <Form.Message compact={false} />
          </Form.Item>
        )}
      />

      <SessionStartTimeField control={form.control} />

      <SessionDurationField control={form.control} />

      <SessionCoachField control={form.control} />

      {children}
    </Form.Provider>
  );
};
